/**
 * RichDetails — expandable detail panel of a backend tool event: query,
 * params, result lists (documents, sources, articles Légifrance, structure du
 * dossier), subagent prompt/response, Case Wiki state. Rendered by the
 * ToolGroup expanded view (tool lines and subagent cards). Pure presentation
 * helpers (labels, badges, params) live in toolPresentation.ts.
 */

import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { AlertTriangle, Circle, CircleCheck, ExternalLink, FileText, FolderOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WIKI_SECTION_NOUNS, WIKI_COUNTS_LABELS, WIKI_STATE_LABELS, objectiveChecklist, type ObjectiveChecklistItem } from './toolPresentation';
import type { BackendTool } from '../../types/websocket.types';

export function RichDetails({ event }: { event: BackendTool['payload'] }) {
  const d = event.data;
  if (!d) return null;

  const sections: React.ReactNode[] = [];

  const checklist = objectiveChecklist(event);
  if (checklist !== null) {
    sections.push(<ObjectiveChecklist key="objective" title={typeof d.title === 'string' ? d.title : undefined} items={checklist} />);
  }

  // Query shown prominently at the top
  if (d.query) {
    sections.push(
      <div key="query" className="text-xs text-grey-600 italic leading-relaxed">
        « {d.query} »
      </div>
    );
  }

  // Other params (excluding query which is shown above)
  const params: Array<{ label: string; value: string }> = [];
  if (d.document_title) params.push({ label: 'Document', value: d.document_title });
  if (d.document_id && !d.document_title) params.push({ label: 'ID', value: d.document_id });
  if (d.url) params.push({ label: 'URL', value: d.url });
  if (d.filename) params.push({ label: 'Fichier', value: d.filename });
  if (d.articleId) params.push({ label: 'Article', value: d.articleId });
  if (d.code_name) params.push({ label: 'Code', value: d.code_name });
  if (d.path) params.push({ label: 'Chemin', value: d.path });
  if (d.include_domains?.length) params.push({ label: 'Domaines', value: d.include_domains.join(', ') });
  if (d.source) params.push({ label: 'Source', value: d.source === 'synthesis' ? 'Synthèse' : 'Original' });

  if (params.length > 0) {
    sections.push(<KeyValueSection key="params" label="Paramètres" items={params} />);
  }

  // Documents list (search_case_documents)
  if (d.documents?.length) {
    sections.push(
      <div key="documents">
        <SectionLabel>Documents trouvés</SectionLabel>
        <div className="mt-1 space-y-0.5">
          {d.documents.map((doc, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-grey-600">
              <FileText className="w-3 h-3 text-grey-400 flex-shrink-0" />
              <span className="truncate" title={doc}>{doc}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Sources list (web_search, search_doctrine)
  if (d.sources?.length) {
    sections.push(
      <div key="sources">
        <SectionLabel>Sources</SectionLabel>
        <div className="mt-1 space-y-0.5">
          {d.sources.map((source, i) => (
            <a
              key={i}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-accent-blue hover:text-accent-blue/80 hover:underline"
              title={source.url}
            >
              <ExternalLink className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{source.title}</span>
            </a>
          ))}
        </div>
      </div>
    );
  }

  // Legifrance results (articles de code, législation)
  if (Array.isArray(d.results) && d.results.length > 0) {
    sections.push(
      <div key="results">
        <SectionLabel>Résultats</SectionLabel>
        <div className="mt-1 space-y-0.5">
          {d.results.map((r, i) => (
            <ArticleResultRow key={i} result={r} />
          ))}
        </div>
      </div>
    );
  }

  // Legifrance codes list
  if (d.codes?.length) {
    const preview = d.codes.slice(0, 5);
    sections.push(
      <div key="codes">
        <SectionLabel>Codes ({d.count ?? d.codes.length})</SectionLabel>
        <div className="mt-1 space-y-0.5">
          {preview.map((c, i) => (
            <div key={i} className="text-xs text-grey-600">{c.title}</div>
          ))}
          {d.codes.length > 5 && (
            <div className="text-xs text-grey-400">+ {d.codes.length - 5} autres</div>
          )}
        </div>
      </div>
    );
  }

  // Case structure — flat children list, whole subtree of the requested path
  if (d.children?.length) {
    sections.push(
      <div key="children">
        <SectionLabel>{d.path || '/'}</SectionLabel>
        <div className="mt-1 space-y-0.5">
          {d.children.map((child, i) => {
            const isFile = child.type === 'file';
            return (
              <div key={i} className="flex items-center gap-1.5 text-xs text-grey-600">
                {isFile ? (
                  <FileText className="w-3 h-3 text-grey-400 flex-shrink-0" />
                ) : (
                  <FolderOpen className="w-3 h-3 text-grey-400 flex-shrink-0" />
                )}
                <span className="truncate" title={child.name}>
                  {child.pieceNumber ? `${child.pieceNumber} · ` : ''}
                  {child.name}
                </span>
                {child.id && (
                  <span className="text-[9px] text-grey-400 flex-shrink-0 font-mono">{child.id}</span>
                )}
                {child.documentType && (
                  <span className="text-[9px] text-grey-400 flex-shrink-0 uppercase">{child.documentType}</span>
                )}
                {!isFile && child.documentCount !== undefined && (
                  <span className="text-xs text-grey-400 ml-auto flex-shrink-0">{child.documentCount}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Completed article details (legifrance_get_article)
  if (event.tool === 'legifrance_get_article' && event.status === 'completed') {
    const articleParams: Array<{ label: string; value: string }> = [];
    if (d.num) articleParams.push({ label: 'Numéro', value: `Art. ${d.num}` });
    if (d.document_title) articleParams.push({ label: 'Texte', value: d.document_title });
    if (d.etat) articleParams.push({ label: 'État', value: d.etat });
    if (d.section_path) articleParams.push({ label: 'Section', value: d.section_path });
    if (articleParams.length > 0) {
      sections.push(<KeyValueSection key="article-detail" label="Article" items={articleParams} isResult />);
    }
    if (d.content) {
      const preview = d.content;
      sections.push(
        <div key="article-content">
          <SectionLabel>Contenu</SectionLabel>
          <div className="mt-1 text-xs text-grey-600 leading-relaxed whitespace-pre-wrap italic border-l-2 border-grey-200 pl-3 py-1 bg-grey-50/50 rounded-r">
            {preview}
          </div>
        </div>
      );
    }
  }

  // Subagent invocation (invoke_subagent) — prompt envoyé + réponse reçue
  if (event.tool === 'invoke_subagent') {
    const agentName = typeof d.agent_name === 'string' ? d.agent_name : null;
    const prompt = typeof d.prompt === 'string' ? d.prompt : null;
    const response = typeof d.response === 'string' ? d.response : null;
    const errorMessage = typeof d.error_message === 'string' ? d.error_message : null;
    const subagentSources = Array.isArray(d.sources) ? (d.sources as Array<{ type?: string; ref?: string; url?: string }>) : [];
    const warnings = Array.isArray(d.warnings) ? (d.warnings as string[]) : [];
    const iterations = typeof d.iterations === 'number' ? d.iterations : null;

    if (agentName) {
      sections.push(
        <div key="subagent-header" className="flex items-baseline gap-2 text-xs">
          <span className="text-grey-400 flex-shrink-0">Sous-agent</span>
          <span className="text-grey-600 font-medium">{agentName}</span>
          {iterations !== null && (
            <span className="text-grey-400 ml-auto text-xs">
              {iterations} itération{iterations > 1 ? 's' : ''}
            </span>
          )}
        </div>
      );
    }

    if (prompt) {
      sections.push(
        <div key="subagent-prompt">
          <ExpandableText text={prompt} label="Consigne envoyée" />
        </div>
      );
    }

    if (response) {
      sections.push(
        <div key="subagent-response">
          <ExpandableText text={response} label="Réponse reçue" />
        </div>
      );
    }

    if (warnings.length > 0) {
      sections.push(
        <div key="subagent-warnings">
          <SectionLabel>Alertes</SectionLabel>
          <div className="mt-1 space-y-0.5">
            {warnings.map((w, i) => (
              <div key={i} className="text-xs text-amber-700">⚠ {w}</div>
            ))}
          </div>
        </div>
      );
    }

    if (subagentSources.length > 0) {
      sections.push(
        <div key="subagent-sources">
          <SectionLabel>Sources citées ({subagentSources.length})</SectionLabel>
          <div className="mt-1 space-y-0.5">
            {subagentSources.map((src, i) => {
              if (!src.ref) return null;
              return (
                <div key={i} className="flex items-center gap-1.5 text-xs text-grey-600">
                  {src.url ? (
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-accent-blue hover:text-accent-blue/80 hover:underline"
                      title={src.url}
                    >
                      <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{src.ref}</span>
                    </a>
                  ) : (
                    <span className="truncate" title={src.ref}>
                      {src.type ? `[${src.type}] ` : ''}{src.ref}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (errorMessage) {
      sections.push(
        <div key="subagent-error" className="text-xs text-red-600">
          {errorMessage}
        </div>
      );
    }
  }

  // Case Wiki overview (get_case_wiki_overview) — état + compteurs + pièces.
  if (event.tool === 'get_case_wiki_overview') {
    const meta: Array<{ label: string; value: string }> = [];
    if (d.state) meta.push({ label: 'État', value: WIKI_STATE_LABELS[d.state] ?? d.state });
    if (d.pieces_analyzed !== undefined && d.total_pieces !== undefined) {
      meta.push({ label: 'Pièces analysées', value: `${d.pieces_analyzed} / ${d.total_pieces}` });
    } else if (d.total_pieces !== undefined) {
      meta.push({ label: 'Pièces', value: String(d.total_pieces) });
    }
    if (meta.length > 0) {
      sections.push(<KeyValueSection key="wiki-overview-meta" label="Analyse avancée" items={meta} isResult />);
    }
    // Compteurs d'entités : on rend l'objet `counts` (clé→libellé humain) tel quel.
    if (d.counts) {
      const countItems = Object.entries(d.counts)
        .filter(([, v]) => typeof v === 'number')
        .map(([key, v]) => ({ label: WIKI_COUNTS_LABELS[key] ?? key, value: String(v) }));
      if (countItems.length > 0) {
        sections.push(<KeyValueSection key="wiki-overview-counts" label="Contenu du wiki" items={countItems} isResult />);
      }
    }
  }

  // Case Wiki section read (read_case_wiki) — section + mode + nombre lu.
  if (event.tool === 'read_case_wiki') {
    const meta: Array<{ label: string; value: string }> = [];
    if (d.section) meta.push({ label: 'Section', value: WIKI_SECTION_NOUNS[d.section] ?? d.section });
    if (d.mode) meta.push({ label: 'Précision', value: d.mode === 'detail' ? 'Détaillée' : d.mode === 'brief' ? 'Résumée' : d.mode });
    if (d.count !== undefined) meta.push({ label: 'Éléments lus', value: String(d.count) });
    if (meta.length > 0) {
      sections.push(<KeyValueSection key="wiki-read-meta" label="Lecture du wiki" items={meta} isResult />);
    }
  }

  // Generic scalar results
  const scalarResults: Array<{ label: string; value: string }> = [];
  if (d.truncated) scalarResults.push({ label: 'Tronqué', value: 'Oui' });
  if (typeof d.error === 'string') scalarResults.push({ label: 'Erreur', value: d.error });

  if (scalarResults.length > 0) {
    sections.push(<KeyValueSection key="scalar" label="Détails" items={scalarResults} isResult />);
  }

  if (sections.length === 0) return null;

  return <div className="px-3 py-2 space-y-2.5">{sections}</div>;
}

// ─── Legifrance article row with hover preview ─────────────────────────────

interface ArticleResult {
  id?: string;
  title: string;
  num: string;
  etat: string;
  preview?: string;
  nature?: string;
  dateSignature?: string | null;
}

function ArticleResultRow({ result: r }: { result: ArticleResult }) {
  const [showPreview, setShowPreview] = useState(false);
  const hasPreview = !!r.preview;

  return (
    <div
      className="relative"
      onMouseEnter={hasPreview ? () => setShowPreview(true) : undefined}
      onMouseLeave={hasPreview ? () => setShowPreview(false) : undefined}
    >
      <div className={cn("text-xs text-grey-600 flex items-baseline gap-1 flex-wrap", hasPreview && "cursor-help")}>
        <span className="font-medium">Art. {r.num}</span>
        <span className="text-grey-400">· {r.title}</span>
        <span className={cn("text-xs", r.etat === 'ABROGE' ? 'text-amber-600' : 'text-grey-400')}>{r.etat}</span>
        {r.nature && <span className="text-grey-400 text-xs">({r.nature})</span>}
        {r.dateSignature && <span className="text-grey-400 text-xs">{r.dateSignature}</span>}
      </div>
      {/* Hover tooltip with article preview */}
      {showPreview && r.preview && (
        <div className="absolute left-0 top-full z-50 mt-1 max-w-md rounded-md border border-grey-200 bg-white p-2.5 shadow-lg">
          <div
            className="text-xs text-grey-600 leading-relaxed [&_mark]:bg-yellow-100 [&_mark]:px-0.5 [&_mark]:rounded-sm"
            // DOMPurify: le preview backend contient du HTML Légifrance avec des <mark> pour le highlighting —
            // sanitize avec allowlist restrictive pour bloquer tout XSS depuis du contenu JP tiers
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(r.preview, {
              ALLOWED_TAGS: ['mark', 'b', 'strong', 'em', 'i', 'br', 'p', 'span'],
              ALLOWED_ATTR: ['class'],
            }) }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Shared components ──────────────────────────────────────────────────────

// 300c truncate + "voir plus" / "voir moins" — utilisé pour le prompt envoyé
// au sous-agent et la réponse reçue, qui peuvent être très longs.
const TRUNCATE_LENGTH = 300;

function ExpandableText({ text, label }: { text: string; label?: string }) {
  const [expanded, setExpanded] = useState(false);
  const needsTruncation = text.length > TRUNCATE_LENGTH;
  const displayed = !needsTruncation || expanded ? text : `${text.slice(0, TRUNCATE_LENGTH)}…`;

  return (
    <div className="text-xs text-grey-600 leading-relaxed whitespace-pre-wrap italic border-l-2 border-grey-200 pl-3 py-1 bg-grey-50/50 rounded-r">
      {label && (
        <div className="not-italic font-semibold text-grey-400 uppercase tracking-widest text-xs mb-1">
          {label}
        </div>
      )}
      {displayed}
      {needsTruncation && (
        <button
          type="button"
          className="ml-1 text-accent-blue hover:text-accent-blue/80 hover:underline cursor-pointer not-italic"
          onClick={() => setExpanded(v => !v)}
        >
          {expanded ? 'voir moins' : 'voir plus'}
        </button>
      )}
    </div>
  );
}

const OBJECTIVE_ITEM_ICON = {
  done: <CircleCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-green-600" aria-label="Fait" />,
  blocked: <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" aria-label="En attente de vous" />,
  todo: <Circle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-grey-400" aria-label="À faire" />,
} satisfies Record<ObjectiveChecklistItem['state'], React.ReactNode>;

/** Les critères d'un objectif en liste à cocher : état, libellé, puis la mesure ou le motif de blocage. */
function ObjectiveChecklist({ title, items }: { title?: string; items: ObjectiveChecklistItem[] }) {
  const faits = items.filter((item) => item.state === 'done').length;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium text-grey-700">{title ?? 'Critères de l’objectif'}</span>
        <span className="text-xs text-grey-400 flex-shrink-0">{faits} sur {items.length}</span>
      </div>
      <ul className="mt-1.5 space-y-1.5">
        {items.map((item) => (
          <li key={item.label} className="flex gap-2 text-xs">
            {OBJECTIVE_ITEM_ICON[item.state]}
            <div className="min-w-0">
              <div className={item.state === 'todo' ? 'text-grey-500' : 'text-grey-700'}>{item.label}</div>
              {item.detail && (
                <div className={item.state === 'blocked' ? 'text-amber-700' : 'text-grey-500'}>{item.detail}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs font-semibold text-grey-400 uppercase tracking-widest">
      {children}
    </span>
  );
}

function KeyValueSection({
  label,
  items,
  isResult,
}: {
  label: string;
  items: Array<{ label: string; value: string }>;
  isResult?: boolean;
}) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="mt-1 space-y-0.5">
        {items.map(item => (
          <div key={item.label} className="flex items-baseline gap-2 text-xs">
            <span className="text-grey-400 flex-shrink-0">{item.label}</span>
            <span
              className={cn('truncate', isResult ? 'text-grey-600 font-medium' : 'text-grey-600')}
              title={item.value}
            >
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
